import { Page } from '@web/components/page';
import { withServerProps } from '@web/library/with-server-props';
import {
  IndexListingsScreen,
  IndexListingsScreenProps,
  listingPageTranslationPaths,
} from 'shared/screens/listings/index-listings';
import { useTranslation } from 'react-i18next';
import { PageBuilder } from 'shared/library/page-builder';
import { Animal, Listing, PetAge } from 'shared/@generated/models';
import { Paginator } from 'shared/@types';
import {
  ListingsFilter,
  sanitizeListingsFilterParams,
  shouldNoIndexListingsFilter,
} from 'shared/state/listings-filter';
import { apiRoute } from 'shared/library/api-route';
import { prepareDataForApi } from 'shared/library/api-client';
import {
  compactListingCardList,
  compactListingCardPaginator,
} from 'shared/library/compact-page-data';
import { useRoute } from 'shared/library/routes/route';
import { useListingIndexResults } from '@web/library/use-listing-index-results';

type ListingCategoryConfig = {
  animal: Animal;
  petAge?: PetAge;
  translationKey: string;
};

type ListingCategoryParams = ListingsFilter;

function getCategoryParams(params: ListingCategoryParams, config: ListingCategoryConfig) {
  return {
    ...params,
    animal: config.animal,
    ...(config.petAge ? { petAge: config.petAge } : {}),
  };
}

export function createListingCategoryPage(config: ListingCategoryConfig) {
  const lockedFilters = {
    animal: config.animal,
    ...(config.petAge ? { petAge: config.petAge } : {}),
  };

  return function ListingCategoryPage(serverProps: IndexListingsScreenProps) {
    const { t } = useTranslation('screens');
    const route = useRoute();
    const props = useListingIndexResults(serverProps, lockedFilters, route('listings.index'));
    const title = t(`listings.index_listings.categories.${config.translationKey}.title`);
    const description = t(
      `listings.index_listings.categories.${config.translationKey}.description`,
    );
    const ignoredNoIndexFilters: (keyof ListingsFilter)[] = ['animal'];

    if (config.petAge) {
      ignoredNoIndexFilters.push('petAge');
    }

    const shouldNoIndex = shouldNoIndexListingsFilter(props.initialFilter, ignoredNoIndexFilters);

    return (
      <Page
        {...props}
        register={(seo) => {
          const pageSeo = seo
            .setTitle(title)
            .setDescription(description)
            .setPagination(props.initialListings);

          return shouldNoIndex ? pageSeo.setNoIndex() : pageSeo;
        }}
      >
        <IndexListingsScreen
          {...props}
          title={title}
          subtitle={description}
          queryPathname={route('listings.index')}
        />
      </Page>
    );
  };
}

export function createListingCategoryPageBuilder(config: ListingCategoryConfig) {
  return new PageBuilder<IndexListingsScreenProps, ListingCategoryParams>()
    .translationPaths(listingPageTranslationPaths)
    .request(({ req, params }) => {
      const categoryParams = sanitizeListingsFilterParams(getCategoryParams(params, config));

      return req<{ listings: Paginator<Listing>; total_pets: number }>(apiRoute('listings.index'), {
        ...prepareDataForApi(categoryParams, ['breeds']),
        format: 'card',
      }).then((res) => ({
        initialListings: compactListingCardPaginator(res.listings),
        totalPets: res.total_pets,
        initialFilter: categoryParams,
      }));
    })
    .request(({ req, params }) => {
      const categoryParams = sanitizeListingsFilterParams(getCategoryParams(params, config));

      return req<{ boosted_listings: Listing[] }>(apiRoute('listings.boosted'), {
        ...prepareDataForApi(categoryParams, ['breeds']),
        format: 'card',
      }).then((res) => ({
        boostedListings: compactListingCardList(res.boosted_listings),
      }));
    });
}

export function createListingCategoryGetServerSideProps(config: ListingCategoryConfig) {
  return withServerProps(createListingCategoryPageBuilder(config));
}
